package io.janni.wedding.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.BackgroundSize
import com.varabyte.kobweb.compose.css.functions.url
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.toAttrs
import io.janni.wedding.components.sections.Footer
import io.janni.wedding.components.widgets.IconButton
import io.janni.wedding.viewmodels.SlideshowViewModel
import io.janni.wedding.viewmodels.backgroundImages
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaChevronRight
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.koin.compose.viewmodel.koinViewModel
import org.w3c.dom.HTMLImageElement

val PageContentStyle = CssStyle {
    base { Modifier.fillMaxSize().padding(leftRight = 2.cssRem, top = 4.cssRem) }
}

// CSS for fade-in animation
private const val fadeInKeyframes = """
    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
"""

class PageLayoutData(val title: String)

@Composable
@Layout
fun PageLayout(ctx: PageContext, content: @Composable ColumnScope.() -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    val viewModel = koinViewModel<SlideshowViewModel>()

    LaunchedEffect(data.title) {
        document.title = "Hochzeit - Lisa & Janni"
    }

    // Inject CSS keyframes for fade animation
    LaunchedEffect(Unit) {
        if (document.getElementById("fadeInKeyframes") == null) {
            val style = document.createElement("style")
            style.id = "fadeInKeyframes"
            style.textContent = fadeInKeyframes
            document.head?.appendChild(style)
        }
    }

    // Prefetch all images on component mount
    LaunchedEffect(Unit) {
        backgroundImages.forEach { imagePath ->
            val img = document.createElement("img") as HTMLImageElement
            img.src = imagePath
        }
    }

    LaunchedEffect(Unit) {
        viewModel.startSlideshow()
    }

    val buttonColor = Colors.White

    Box(
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .position(Position.Relative)
    ) {
        // Layer A - alternates with Layer B
        Box(
            Modifier
                .fillMaxSize()
                .position(Position.Absolute)
                .top(0.px)
                .left(0.px)
                .background {
                    image(url(backgroundImages[viewModel.layerAIndex]))
                    size(BackgroundSize.Cover)
                }
                .styleModifier {
                    property("background-position", "center")
                    property("z-index", "0")
                    property("opacity", if (viewModel.showLayerA) "1" else "0")
                    property("transition", "opacity 1.5s ease-in-out")
                }
        )

        // Layer B - alternates with Layer A
        Box(
            Modifier
                .fillMaxSize()
                .position(Position.Absolute)
                .top(0.px)
                .left(0.px)
                .background {
                    image(url(backgroundImages[viewModel.layerBIndex]))
                    size(BackgroundSize.Cover)
                }
                .styleModifier {
                    property("background-position", "center")
                    property("z-index", "0")
                    property("opacity", if (!viewModel.showLayerA) "1" else "0")
                    property("transition", "opacity 1.5s ease-in-out")
                }
        )

        // Content layer (not affected by opacity transition)
        Box(
            Modifier
                .fillMaxWidth()
                .minHeight(100.vh)
                .position(Position.Relative)
                .styleModifier {
                    property("z-index", "2")
                }
                // Create a box with two rows: the main content (fills as much space as it can) and the footer (which reserves
                // space at the bottom). "min-content" means the use the height of the row, which we use for the footer.
                // Since this box is set to *at least* 100%, the footer will always appear at least on the bottom but can be
                // pushed further down if the first row grows beyond the page.
                // Grids are powerful but have a bit of a learning curve. For more info, see:
                // https://css-tricks.com/snippets/css/complete-guide-grid/
                .gridTemplateRows { size(1.fr); size(minContent) },
            contentAlignment = Alignment.Center
        ) {
        // Previous button
        Box(
            Modifier
                .position(Position.Absolute)
                .top(50.percent)
                .left(0.5.cssRem)
                .styleModifier {
                    property("transform", "translateY(-50%)")
                }
        ) {
            IconButton(
                onClick = { viewModel.previousImage() }
            ) {
                Box(
                    Modifier
                        .width(3.cssRem)
                        .height(3.cssRem)
                        .backgroundColor(buttonColor.copy(alpha = 50))
                        .borderRadius(50.percent)
                        .fontSize(1.5.cssRem)
                        .styleModifier {
                            property("backdrop-filter", "blur(10px)")
                            property("-webkit-backdrop-filter", "blur(10px)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("line-height", "1")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    FaChevronLeft()
                }
            }
        }

        // Next button
        Box(
            Modifier
                .position(Position.Absolute)
                .top(50.percent)
                .right(0.5.cssRem)
                .styleModifier {
                    property("transform", "translateY(-50%)")
                }
        ) {
            IconButton(
                onClick = { viewModel.nextImage() }
            ) {
                Box(
                    Modifier
                        .width(3.cssRem)
                        .height(3.cssRem)
                        .backgroundColor(buttonColor.copy(alpha = 50))
                        .borderRadius(50.percent)
                        .fontSize(1.5.cssRem)
                        .styleModifier {
                            property("backdrop-filter", "blur(10px)")
                            property("-webkit-backdrop-filter", "blur(10px)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("line-height", "1")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    FaChevronRight()
                }
            }
        }

        // Slide indicators
        Row(
            Modifier
                .position(Position.Absolute)
                .bottom(2.cssRem)
                .gap(0.5.cssRem),
            verticalAlignment = Alignment.CenterVertically
        ) {
            backgroundImages.forEachIndexed { index, _ ->
                Box(
                    Modifier
                        .width(12.px)
                        .height(12.px)
                        .borderRadius(50.percent)
                        .backgroundColor(
                            if (index == viewModel.currentImageIndex) buttonColor.copy(alpha = 200)
                            else buttonColor.copy(alpha = 80)
                        )
                        .styleModifier {
                            property("transition", "background-color 0.3s ease")
                            property("cursor", "pointer")
                        }
                        .onClick { viewModel.setImageIndex(index) }
                )
            }
        }
        Column(
            // Isolate the content, because otherwise the absolute-positioned SVG above will render on top of it.
            // This is confusing but how browsers work. Read up on stacking contexts for more info.
            // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_context
            // Some people might have used z-index instead, but best practice is to avoid that if possible, because
            // as a site gets complex, Z-fighting can be a huge pain to track down.
            Modifier.fillMaxSize().gridRow(1),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
//            NavHeader()
            Div(PageContentStyle.toAttrs()) {
                content()
            }
        }
        // Associate the footer with the row that will get pushed off the bottom of the page if it can't fit.
        Footer(Modifier.fillMaxWidth().gridRow(2))
        }
    }
}
